import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mic, Send, Boxes, Plus, TrendingUp, Trash2, Check } from "lucide-react";
import { parseRestockCommand, applyRestock, formatMXN, normalizeTTS } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLES = [
  "Mete al inventario 3 cajas de coca cola mediana",
  "Integra 8 kilos de huevo y 5 kilos de frijol",
  "Suma al inventario 10 cuadernos scribe de raya profesional",
  "Traje 2 docenas de sabritas y 6 cervezas Corona",
];

const UNITS = ["pieza", "kg", "g", "l", "ml", "docena", "caja", "paquete"];
const CATEGORIES = ["Bebidas", "Abarrotes Secos", "Panadería", "Lácteos", "Botanas", "Huevo", "Limpieza", "Carnes", "Frutas y Verduras", "Escolar", "Oficina", "Impresión", "Aceites", "Filtros", "Eléctrico", "Frenos", "Otros"];

/**
 * Voice-driven inventory restock modal. Parses the utterance locally (0
 * tokens), shows an editable preview and applies with a single API call.
 */
export default function RestockModal({ open, onClose, onApplied }) {
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [tts, setTts] = useState("");
  const [recording, setRecording] = useState(false);
  const [applying, setApplying] = useState(false);
  const [parsing, setParsing] = useState(false);
  const recRef = useRef(null);

  useEffect(() => {
    if (!open) { setText(""); setItems([]); setTts(""); }
    return () => { try { recRef.current?.stop(); } catch { /* noop */ } };
  }, [open]);

  const doParse = async (t) => {
    const v = (t || text).trim();
    if (!v) return;
    setParsing(true);
    try {
      const r = await parseRestockCommand(v);
      setItems(r.items || []);
      setTts(r.tts_message || "");
      if (!r.items?.length) toast.error("No detecté productos. Prueba: 'Mete 3 cajas de coca'");
      if (window.speechSynthesis && r.tts_message) {
        const spoken = await normalizeTTS(r.tts_message);
        const u = new SpeechSynthesisUtterance(spoken);
        u.lang = "es-MX"; u.rate = 1.05; window.speechSynthesis.speak(u);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al procesar"); }
    finally { setParsing(false); }
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Este navegador no soporta dictado por voz"); return; }
    if (recording) { try { recRef.current?.stop(); } catch { /* noop */ } setRecording(false); return; }
    const rec = new SR();
    rec.lang = "es-MX";
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setText(t);
      setRecording(false);
      doParse(t);
    };
    rec.onerror = () => setRecording(false);
    rec.onend = () => setRecording(false);
    try { rec.start(); recRef.current = rec; setRecording(true); }
    catch { /* noop */ }
  };

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch, new_stock: (it.current_stock || 0) + Number(patch.add_quantity ?? it.add_quantity) } : it));
  };

  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const apply = async () => {
    const valid = items.filter((i) => i.add_quantity > 0 && (i.name || "").trim());
    if (!valid.length) { toast.error("No hay artículos válidos"); return; }
    setApplying(true);
    try {
      const r = await applyRestock(valid.map((it) => ({
        action: it.action,
        product_id: it.product_id || undefined,
        name: it.name,
        unit: it.unit,
        category: it.category,
        add_quantity: Number(it.add_quantity),
        sale_price: Number(it.sale_price || 0),
        cost_price: Number(it.cost_price || 0),
      })));
      toast.success(r.tts_message || "Inventario actualizado");
      if (window.speechSynthesis && r.tts_message) {
        const spoken = await normalizeTTS(r.tts_message);
        const u = new SpeechSynthesisUtterance(spoken);
        u.lang = "es-MX"; window.speechSynthesis.speak(u);
      }
      onApplied?.(r);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se aplicó"); }
    finally { setApplying(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose?.()}
          data-testid="restock-modal"
        >
          <motion.div
            initial={{ y: 20, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-[#0a0a0a] border border-white/10 rounded-3xl"
          >
            <button onClick={onClose} className="absolute top-4 right-4 z-10 text-white/40 hover:text-white p-2 rounded-full" data-testid="restock-close">
              <X size={18} />
            </button>

            {/* Header */}
            <div className="p-6 md:p-8 border-b border-white/10">
              <div className="chip mb-3"><Boxes size={12} className="text-[#ccff00]" /> Meter al inventario · Voz</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tighter leading-none">
                Habla y <span className="text-[#ccff00]">surte</span> tu tienda.
              </h2>
              <p className="text-white/50 text-sm mt-2">
                Di algo como "Chalan, mete al inventario 3 cajas de coca mediana y 8 kilos de huevo".
              </p>
            </div>

            {/* Input row */}
            <div className="p-6 md:p-8 border-b border-white/10">
              <div className="flex items-stretch gap-2">
                <button
                  onClick={toggleMic}
                  className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center border-2 transition-colors ${recording ? "border-[#ccff00] bg-[#ccff00]/15 text-[#ccff00]" : "border-[#ff5a00] bg-[#ff5a00]/10 text-[#ff5a00] hover:bg-[#ff5a00]/20"}`}
                  data-testid="restock-mic"
                  title="Dictar por voz"
                >
                  <Mic size={22} />
                </button>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") doParse(); }}
                  placeholder='Escribe o dicta: "Mete 3 cajas de coca mediana, 8 kilos de huevo"'
                  className="flex-1 input"
                  data-testid="restock-input"
                />
                <button
                  onClick={() => doParse()}
                  disabled={parsing || !text.trim()}
                  className="px-5 rounded-2xl bg-white text-black font-display text-lg tracking-tighter flex items-center gap-2 disabled:opacity-50"
                  data-testid="restock-parse"
                >
                  {parsing ? "…" : (<><Send size={16} /> Detectar</>)}
                </button>
              </div>
              {tts && <p className="mt-3 text-white/60 text-sm italic">{tts}</p>}
              {!items.length && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {EXAMPLES.map((ex) => (
                    <button key={ex} onClick={() => { setText(ex); doParse(ex); }} className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10 text-white/50 hover:text-white hover:border-white/30" data-testid="restock-example">
                      {ex}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8" data-testid="restock-preview">
              {items.length === 0 ? (
                <p className="text-center text-white/40 text-sm py-8">Los artículos detectados aparecerán aquí para que los confirmes.</p>
              ) : (
                <div className="space-y-3">
                  {items.map((it, i) => (
                    <div key={i} className={`border rounded-2xl p-4 ${it.action === "create" ? "border-[#ccff00]/30 bg-[#ccff00]/[0.03]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`restock-item-${i}`}>
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          {it.action === "create" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ccff00]/15 text-[#ccff00] text-[10px] font-mono uppercase tracking-widest">
                              <Plus size={10} /> Nuevo producto
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/60 text-[10px] font-mono uppercase tracking-widest">
                              <TrendingUp size={10} /> Aumenta stock
                            </span>
                          )}
                          {it.container && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                              detectado: {it.container}
                            </span>
                          )}
                        </div>
                        <button onClick={() => removeItem(i)} className="text-white/30 hover:text-red-400 p-1" data-testid={`restock-remove-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>

                      <div className="grid grid-cols-12 gap-2">
                        <input
                          value={it.name}
                          onChange={(e) => updateItem(i, { name: e.target.value })}
                          className="col-span-12 md:col-span-5 input"
                          placeholder="Nombre"
                          data-testid={`restock-name-${i}`}
                        />
                        <input
                          type="number" step="0.01" min="0"
                          value={it.add_quantity}
                          onChange={(e) => updateItem(i, { add_quantity: e.target.value })}
                          className="col-span-4 md:col-span-2 input font-mono"
                          data-testid={`restock-qty-${i}`}
                        />
                        <select
                          value={it.unit}
                          onChange={(e) => updateItem(i, { unit: e.target.value })}
                          className="col-span-4 md:col-span-2 input"
                          data-testid={`restock-unit-${i}`}
                        >
                          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <select
                          value={it.category || "Otros"}
                          onChange={(e) => updateItem(i, { category: e.target.value })}
                          className="col-span-4 md:col-span-3 input text-xs"
                          data-testid={`restock-cat-${i}`}
                        >
                          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {it.action === "create" && (
                          <>
                            <label className="col-span-6 md:col-span-3 text-[10px] font-mono uppercase tracking-widest text-white/40">
                              Precio venta
                              <input
                                type="number" step="0.5" min="0"
                                value={it.sale_price || ""}
                                onChange={(e) => updateItem(i, { sale_price: e.target.value })}
                                placeholder="$"
                                className="input mt-1 font-mono"
                                data-testid={`restock-price-${i}`}
                              />
                            </label>
                            <label className="col-span-6 md:col-span-3 text-[10px] font-mono uppercase tracking-widest text-white/40">
                              Costo
                              <input
                                type="number" step="0.5" min="0"
                                value={it.cost_price || ""}
                                onChange={(e) => updateItem(i, { cost_price: e.target.value })}
                                placeholder="$"
                                className="input mt-1 font-mono"
                                data-testid={`restock-cost-${i}`}
                              />
                            </label>
                          </>
                        )}
                      </div>

                      <div className="mt-3 flex items-center justify-between text-xs text-white/50 font-mono">
                        <span>Stock actual: <span className="text-white">{it.current_stock}</span> {it.unit}</span>
                        <span>Nuevo stock: <span className="text-[#ccff00] font-bold">{Number(it.current_stock || 0) + Number(it.add_quantity || 0)}</span> {it.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 md:p-8 border-t border-white/10 flex items-center justify-between gap-4">
              <p className="text-white/40 text-xs">
                {items.length > 0 ? `${items.filter((i) => i.action === "increment").length} aumentos · ${items.filter((i) => i.action === "create").length} nuevos` : "—"}
              </p>
              <button
                onClick={apply}
                disabled={applying || !items.length}
                className="px-6 py-3 rounded-2xl bg-[#ff5a00] hover:bg-[#ff7a30] text-black font-display text-lg tracking-tighter flex items-center gap-2 disabled:opacity-50"
                data-testid="restock-apply"
              >
                {applying ? "Guardando…" : (<><Check size={18} /> Meter al inventario</>)}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
